// demo-ref.ts — playground-signup-card cycle (THI-33, 2026-08-22 spec §B). The whole
// client-side mechanism for carrying a public-playground demo id through sign-up/sign-in into
// attribution: one storage key, small functions, modeled on `lib/post-auth-redirect.ts`'s "one
// key, small functions, documented choice of storage".
const DEMO_REF_KEY = 'embent:demo-ref';

/** A demo's `publicId` is short (`demo_ab12cd`-shaped); this is just a defensive upper bound
 * against storing garbage, mirroring `demoRefSchema`'s own `.max(64)` on the wire. */
const MAX_DEMO_ID_LENGTH = 64;

/** `localStorage`, not `sessionStorage` (unlike `post-auth-redirect.ts`): the sign-up path
 * finishes in a DIFFERENT tab via the verification email, which per-tab storage cannot
 * survive. A stale id is harmless — the API is idempotent and first-write-wins.
 *
 * Every access wrapped in try/catch (Safari private mode throws on `localStorage` access) — a
 * storage failure must never break sign-up. */
export function captureDemoRef(search: string): void {
  const demoId = new URLSearchParams(search).get('demo');
  if (!demoId || demoId.length > MAX_DEMO_ID_LENGTH) return;
  try {
    localStorage.setItem(DEMO_REF_KEY, demoId);
  } catch {
    // Storage blocked (Safari private mode, etc.) — nothing to attribute, sign-up proceeds.
  }
}

export function readDemoRef(): string | null {
  try {
    return localStorage.getItem(DEMO_REF_KEY);
  } catch {
    return null;
  }
}

export function clearDemoRef(): void {
  try {
    localStorage.removeItem(DEMO_REF_KEY);
  } catch {
    // Nothing to clean up if storage isn't reachable in the first place.
  }
}

/** Appends `?demo=`/`&demo=` when `demoId` is non-null, else returns `path` unchanged — used
 * to carry the id through the Google OAuth `callbackURL` round trip (same-origin, so the
 * `localStorage` write already survives it; this is the belt-and-braces half for when storage
 * was blocked or cleared). */
export function withDemoRef(path: string, demoId: string | null): string {
  if (!demoId) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}demo=${demoId}`;
}
